import React, { useState, useMemo } from "react";
import { Input } from "antd";
import { SearchOutlined, AppstoreOutlined } from "@ant-design/icons";
import { StandardLayout } from "easy-email-extensions";
import { defaultCategories } from "../utils/emailBuilderConfig";

const { Search } = Input;

interface EmailBuilderSidebarProps {
  compact?: boolean;
}

const EmailBuilderSidebar: React.FC<EmailBuilderSidebarProps> = ({
  compact = false,
}) => {
  const [searchTerm, setSearchTerm] = useState("");

  // Filter categories based on search term
  const filteredCategories = useMemo(() => {
    if (!searchTerm.trim()) {
      return defaultCategories;
    }

    const lowerSearch = searchTerm.toLowerCase();

    return defaultCategories
      .map((category) => {
        // Check if category label matches
        const categoryMatches = category.label
          .toLowerCase()
          .includes(lowerSearch);

        // Filter blocks that match the search (cast to any[] for union type)
        const blocks = category.blocks as any[];
        const filteredBlocks = blocks.filter((block) => {
          const title = block.title || "";
          const type = block.type || "";
          return (
            title.toLowerCase().includes(lowerSearch) ||
            type.toLowerCase().includes(lowerSearch)
          );
        });

        // Include category if it matches or has matching blocks
        if (categoryMatches || filteredBlocks.length > 0) {
          return {
            ...category,
            active: true, // Expand matching categories
            blocks: categoryMatches ? category.blocks : filteredBlocks,
          };
        }

        return null;
      })
      .filter(Boolean) as typeof defaultCategories;
  }, [searchTerm]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  return (
    <div className="email-builder-sidebar-wrapper">
      {/* Search Input */}
      <div className="email-builder-search">
        <Search
          placeholder="Search blocks..."
          allowClear
          prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
          onChange={(e) => handleSearch(e.target.value)}
          value={searchTerm}
          style={{ width: "100%" }}
        />
      </div>

      {/* Show message when no results */}
      {searchTerm && filteredCategories.length === 0 ? (
        <div className="email-builder-no-results">
          <AppstoreOutlined style={{ fontSize: 32, color: "#cbd5e1" }} />
          <p>No blocks found for "{searchTerm}"</p>
          <button
            onClick={() => setSearchTerm("")}
            className="email-builder-clear-search"
          >
            Clear search
          </button>
        </div>
      ) : (
        <StandardLayout categories={filteredCategories} compact={compact} />
      )}
    </div>
  );
};

export default EmailBuilderSidebar;
